<template>
  <div v-if="!hasHistory">
    {{ $t('product.detail.changeHistory.entityHasNoChangeHistory') }}
  </div>
  <div v-else-if="loading">
    <vl-loader :message="$t('product.detail.changeHistory.loading')" class="vl-u-display-flex" />
  </div>
  <vl-alert
    v-else-if="failed"
    icon="warning"
    :title="$t('product.detail.changeHistory.error.title')"
    :content="$t('product.detail.changeHistory.error.content')"
    mod-naked
    mod-error
  />
  <vl-properties-list v-else-if="changeHistory">
    <vl-properties-label class="vl-properties-label-custom">{{
      $t('product.detail.changeHistory.currentCopies.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom">{{ changeHistory.currentCopies }}</vl-properties-data>
    <vl-properties-label class="vl-properties-label-custom">{{
      $t('product.detail.changeHistory.previousCopies.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom">{{ changeHistory.previousCopies }}</vl-properties-data>
    <vl-properties-label class="vl-properties-label-custom">{{
      $t('product.detail.changeHistory.newCopies.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom"
      >{{ changeHistory.newCopies }} {{ getPercentage(changeHistory.newCopies) }}</vl-properties-data
    >
    <vl-properties-label class="vl-properties-label-custom">{{
      $t('product.detail.changeHistory.changedCopies.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom"
      >{{ changeHistory.changedCopies }} {{ getPercentage(changeHistory.changedCopies) }}</vl-properties-data
    >
    <vl-properties-label class="vl-properties-label-custom-padded">{{
      $t('product.detail.changeHistory.attributeChanges.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom">{{ changeHistory.attributeChanges }}</vl-properties-data>
    <vl-properties-label class="vl-properties-label-custom-padded">{{
      $t('product.detail.changeHistory.geometryChanges.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom">{{ changeHistory.geometryChanges }}</vl-properties-data>
    <vl-properties-label class="vl-properties-label-custom-padded">{{
      $t('product.detail.changeHistory.attributeAndGeometryChanges.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom">{{
      changeHistory.attributeAndGeometryChanges
    }}</vl-properties-data>
    <vl-properties-label class="vl-properties-label-custom">{{
      $t('product.detail.changeHistory.abolishedCopies.label')
    }}</vl-properties-label>
    <vl-properties-data class="vl-properties-data-custom"
      >{{ changeHistory.abolishedCopies }} {{ getPercentage(changeHistory.abolishedCopies) }}</vl-properties-data
    >
  </vl-properties-list>
</template>
<script lang="ts" setup>
import { ProductChangeHistory } from '@/api/frontoffice/clients';

const props = defineProps<{
  /** Whether the entity keeps history at all. Without it there are no figures to show. */
  hasHistory?: boolean;
  /** Set while the parent is fetching the figures of this entity. */
  loading?: boolean;
  /** Set when the figures could not be calculated, mostly because the query timed out. */
  failed?: boolean;
  /** The figures of this entity, once the parent has fetched them. */
  changeHistory?: ProductChangeHistory | null;
}>();

/** The share of the previous version this number of copies represents. */
const getPercentage = (numerator: number): string => {
  const denominator = props.changeHistory.previousCopies;
  let result = '0';
  if (denominator > 0) {
    const percentage = (numerator / denominator) * 100;
    if (percentage != 0) {
      let roundedPercentage = Math.round(percentage);
      result = roundedPercentage == 0 ? '<1' : `${roundedPercentage}`;
    }
  }
  return `(${result}%)`;
};
</script>

<style lang="scss" scoped>
.vl-properties-label-custom {
  flex: 0 0 30%;
  max-width: 40rem;
}

.vl-properties-label-custom-padded {
  @extend .vl-properties-label-custom;
  padding-left: 5%;
}

.vl-properties-data-custom {
  flex: 0 0 70%;
    min-width: calc(100% - 40rem);
}
</style>
